/**
 * ResourcesByLawChart Component
 *
 * Displays resources by law as a grouped bar chart showing received vs to be raised.
 * Based on the BarChartCard pattern but customized for law resources.
 *
 * Design specs:
 * - Card: 628px x 495px
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px
 * - Bar width: 41px
 * - Bar gap: 5px
 * - Colors: #13D480 (recebido), #DC4426 (para captar)
 * - Chart height: 297px
 * - Legend: 7px circles
 * - Percentage badge: green background with green text
 */

import { LawResources } from "@/types/gestao-recursos.interface";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface ResourcesByLawChartProps {
  data: LawResources[];
  total: number;
  percentage: number;
  period?: string;
  isLoading?: boolean;
}

export function ResourcesByLawChart({
  data,
  total,
  percentage,
  period = "Últimos 7 dias",
  isLoading,
}: ResourcesByLawChartProps) {
  const isPositive = percentage >= 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px] w-[628px] h-[495px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-[16px] font-semibold text-[#F6F6F6]">
            Recursos por Leis
          </h3>
          <div className="bg-[#0C1427] rounded-[20px] px-[10px] py-1">
            <p
              className={`text-[12px] ${
                isPositive ? "text-[#13D480]" : "text-[#DC4426]"
              }`}
            >
              {isPositive ? "+" : ""}
              {percentage}%
            </p>
          </div>
        </div>
        <p className="text-[12px] text-[#888888]">{period}</p>
      </div>

      {/* Total Value */}
      <p className="text-[20px] font-bold text-white mb-4">
        {formatCurrency(total)}
      </p>

      {/* Chart */}
      <div className="flex-1 min-h-[250px]">
        {isLoading ? (
          <Skeleton className="h-full" />
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Sem dados disponíveis
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={250}>
            <BarChart
              data={data}
              barGap={5}
              barCategoryGap={20}
              margin={{ top: 20, right: 0, left: 0, bottom: 60 }}
            >
              <XAxis
                dataKey="law"
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={0}
                textAnchor="middle"
                height={50}
              />
              <YAxis
                tick={{ fill: "#FFFFFF", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => {
                  if (value >= 100000) return `R$${value / 1000}k`;
                  if (value >= 1000) return `R$${value / 1000}k`;
                  return `R$${value}`;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#15203C",
                  border: "1px solid #5D5D5D",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#F6F6F6" }}
                itemStyle={{ color: "#F6F6F6" }}
                formatter={(value: number) => [formatCurrency(value), ""]}
              />
              <Bar
                dataKey="recebido"
                fill="#13D480"
                radius={[4, 4, 0, 0]}
                barSize={41}
              />
              <Bar
                dataKey="paraCaptar"
                fill="#DC4426"
                radius={[4, 4, 0, 0]}
                barSize={41}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-[20px] mt-3">
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#13D480]" />
          <p className="text-[12px] text-white">Recebido</p>
        </div>
        <div className="flex items-center gap-[10px]">
          <div className="size-[7px] rounded-full bg-[#DC4426]" />
          <p className="text-[12px] text-white">Para captar</p>
        </div>
      </div>
    </div>
  );
}
